import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  Chip,
  Skeleton,
  FormControl,
  InputLabel,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Cell,
} from 'recharts';
import { superAdminService, Analytics, PwaInstallStats, ApiUsageStatsResponse, getFeatureUsageStats, FeatureUsageResponse } from '@/services/superAdminService';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f', '#ff6b6b', '#845ec2'];

// localStorage keys for persisting chart settings
const STORAGE_KEY_FEATURE = 'insights_feature_settings';
const STORAGE_KEY_API = 'insights_api_settings';

// Default values
const FEATURE_DEFAULTS = { featureDays: 3, featureScale: 'linear' as 'linear' | 'log' };
const API_DEFAULTS = { apiProvider: '', apiType: '', apiTrigger: '', apiDays: 3, apiGranularity: 'hourly' as 'daily' | 'hourly', apiGroupBy: 'apiType' as 'service' | 'apiType' | 'trigger' | 'statusCode', apiTimezone: 'local' as 'local' | 'utc' };

function loadSettings<T>(key: string, defaults: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return defaults;
}

function saveSettings(key: string, values: Record<string, unknown>) {
  try { localStorage.setItem(key, JSON.stringify(values)); } catch { /* ignore */ }
}

// Module-level flag: once the chart has animated on first load,
// skip animation on subsequent mounts (navigating away and back)
let chartHasAnimated = false;

const InsightsTab: React.FC = () => {
  const muiTheme = useTheme();
  const chartLabelColor = muiTheme.palette.text.secondary;
  const yAxisLabel = useMemo(() => ({ value: 'Requests', angle: -90, position: 'insideLeft' as const, dy: 0, offset: 10, style: { fontSize: 12, fill: chartLabelColor, textAnchor: 'middle' as const } }), [chartLabelColor]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [pwaStats, setPwaStats] = useState<PwaInstallStats | null>(null);

  // Feature usage state — load persisted settings
  const [featureUsage, setFeatureUsage] = useState<FeatureUsageResponse | null>(null);
  const [loadingFeatureUsage, setLoadingFeatureUsage] = useState(false);
  const [featureDays, setFeatureDays] = useState<number>(() => loadSettings(STORAGE_KEY_FEATURE, FEATURE_DEFAULTS).featureDays);
  const [featureScale, setFeatureScale] = useState<'linear' | 'log'>(() => loadSettings(STORAGE_KEY_FEATURE, FEATURE_DEFAULTS).featureScale);

  // API usage chart state — load persisted settings
  const [apiUsage, setApiUsage] = useState<ApiUsageStatsResponse | null>(null);
  const [loadingApiUsage, setLoadingApiUsage] = useState(false);
  const [apiProvider, setApiProvider] = useState<string>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiProvider);
  const [apiType, setApiType] = useState<string>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiType);
  const [apiTrigger, setApiTrigger] = useState<string>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiTrigger);
  const [apiDays, setApiDays] = useState<number>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiDays);
  const [apiGranularity, setApiGranularity] = useState<'daily' | 'hourly'>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiGranularity);
  const [apiGroupBy, setApiGroupBy] = useState<'service' | 'apiType' | 'trigger' | 'statusCode'>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiGroupBy);
  const [apiTimezone, setApiTimezone] = useState<'local' | 'utc'>(() => loadSettings(STORAGE_KEY_API, API_DEFAULTS).apiTimezone);

  useEffect(() => {
    loadAnalytics();
    loadPwaStats();
  }, []);

  useEffect(() => {
    loadFeatureUsage();
  }, [featureDays]);

  // Persist feature settings
  useEffect(() => {
    saveSettings(STORAGE_KEY_FEATURE, { featureDays, featureScale });
  }, [featureDays, featureScale]);

  useEffect(() => {
    loadApiUsage();
  }, [apiProvider, apiType, apiTrigger, apiDays, apiGranularity, apiGroupBy]);

  // Persist API settings
  useEffect(() => {
    saveSettings(STORAGE_KEY_API, { apiProvider, apiType, apiTrigger, apiDays, apiGranularity, apiGroupBy, apiTimezone });
  }, [apiProvider, apiType, apiTrigger, apiDays, apiGranularity, apiGroupBy, apiTimezone]);

  // Reset callbacks
  const resetFeatureSettings = useCallback(() => {
    setFeatureDays(FEATURE_DEFAULTS.featureDays);
    setFeatureScale(FEATURE_DEFAULTS.featureScale);
    localStorage.removeItem(STORAGE_KEY_FEATURE);
  }, []);

  const resetApiSettings = useCallback(() => {
    setApiProvider(API_DEFAULTS.apiProvider);
    setApiType(API_DEFAULTS.apiType);
    setApiTrigger(API_DEFAULTS.apiTrigger);
    setApiDays(API_DEFAULTS.apiDays);
    setApiGranularity(API_DEFAULTS.apiGranularity);
    setApiGroupBy(API_DEFAULTS.apiGroupBy);
    setApiTimezone(API_DEFAULTS.apiTimezone);
    localStorage.removeItem(STORAGE_KEY_API);
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const analyticsData = await superAdminService.getAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadPwaStats = async () => {
    try {
      const stats = await superAdminService.getPwaInstallStats();
      setPwaStats(stats);
    } catch (error) {
      console.error('Failed to load PWA install stats:', error);
    }
  };

  const loadFeatureUsage = async () => {
    try {
      setLoadingFeatureUsage(true);
      const data = await getFeatureUsageStats(featureDays);
      setFeatureUsage(data);
    } catch (error) {
      console.error('Failed to load feature usage:', error);
    } finally {
      setLoadingFeatureUsage(false);
    }
  };

  // Build time series chart data for feature usage (pivot: date -> { date, feature1: count, feature2: count, ... })
  const featureTimeSeriesData = useMemo(() => {
    if (!featureUsage?.timeSeries.length) return [];
    // Get top 5 features by total
    const topFeatures = featureUsage.totals.slice(0, 5).map(t => t.page);
    const dateMap = new Map<string, Record<string, string | number>>();
    for (const entry of featureUsage.timeSeries) {
      if (!topFeatures.includes(entry.page)) continue;
      if (!dateMap.has(entry.date)) {
        dateMap.set(entry.date, { date: entry.date });
      }
      dateMap.get(entry.date)![entry.page] = entry.count;
    }
    return Array.from(dateMap.values()).sort((a, b) => (a.date as string).localeCompare(b.date as string));
  }, [featureUsage]);

  const featureSeriesKeys = useMemo(() => {
    if (!featureUsage?.totals.length) return [];
    return featureUsage.totals.slice(0, 5).map(t => t.page);
  }, [featureUsage]);

  // Stable color map: feature name → color, based on totals order (consistent across bar + line charts)
  const featureColorMap = useMemo(() => {
    const map = new Map<string, string>();
    featureUsage?.totals.forEach((t, i) => {
      map.set(t.page, COLORS[i % COLORS.length]);
    });
    return map;
  }, [featureUsage?.totals]);

  const loadApiUsage = async () => {
    try {
      setLoadingApiUsage(true);
      const data = await superAdminService.getApiUsageStats({
        provider: apiProvider || undefined,
        apiType: apiType || undefined,
        trigger: apiTrigger || undefined,
        days: apiDays,
        granularity: apiGranularity,
        groupBy: apiGroupBy,
      });
      setApiUsage(data);
      // After initial animation completes (~1.5s), disable animation for
      // subsequent re-mounts to prevent the slide-up update animation
      if (!chartHasAnimated && data.stats.length) {
        setTimeout(() => { chartHasAnimated = true; }, 1500);
      }
    } catch (error) {
      console.error('Failed to load API usage:', error);
    } finally {
      setLoadingApiUsage(false);
    }
  };

  // Format X axis labels based on granularity and timezone
  const formatXLabel = (value: string) => {
    if (apiGranularity === 'hourly' && value.includes('T')) {
      // value is UTC bucket: "2025-02-13T14"
      const utcDate = new Date(value + ':00:00Z');
      if (apiTimezone === 'local') {
        const h = utcDate.getHours();
        const ampm = h >= 12 ? 'pm' : 'am';
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const mon = utcDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${mon} ${h12}${ampm}`;
      }
      // UTC display
      const [datePart, hour] = value.split('T');
      const d = new Date(datePart + 'T00:00:00Z');
      const h = parseInt(hour, 10);
      const ampm = h >= 12 ? 'pm' : 'am';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const mon = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      return `${mon} ${h12}${ampm}`;
    }
    // Daily: "2025-02-13" → "Feb 13"
    const d = new Date(value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Derive series keys from the chart data
  const seriesKeys = useMemo(() => {
    if (!apiUsage?.stats.length) return [];
    const keys = new Set<string>();
    for (const row of apiUsage.stats) {
      for (const key of Object.keys(row)) {
        if (key !== 'date') keys.add(key);
      }
    }
    return Array.from(keys);
  }, [apiUsage?.stats]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Platform analytics and API usage
      </Typography>

      {/* Analytics Section */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
        Platform Analytics
      </Typography>

      {loadingAnalytics ? (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rounded" height={80} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  Organizations
                </Typography>
                <Typography variant="h4">{analytics?.totalOrganizations ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  Active Subs
                </Typography>
                <Typography variant="h4">{analytics?.activeSubscriptions ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  Users
                </Typography>
                <Typography variant="h4">{analytics?.totalUsers ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  Revenue
                </Typography>
                <Typography variant="h4">${analytics?.monthlyRevenue ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  PWA Installs
                </Typography>
                <Typography variant="h4">{pwaStats?.totalInstalls ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* PWA Install Details */}
      {pwaStats && pwaStats.totalInstalls > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            PWA Install Breakdown
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Chip label={`Last 30 days: ${pwaStats.last30Days}`} variant="outlined" />
            <Chip label={`From landing: ${pwaStats.bySource.landing}`} variant="outlined" />
            <Chip label={`From app: ${pwaStats.bySource.app}`} variant="outlined" />
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Feature Usage Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Feature Usage
        </Typography>
        <Button size="small" startIcon={<RestartAlt sx={{ fontSize: 16 }} />} onClick={resetFeatureSettings} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
          Reset
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Range</InputLabel>
          <Select value={featureDays} label="Range" onChange={(e) => setFeatureDays(e.target.value as number)}>
            <MenuItem value={3}>3 days</MenuItem>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={14}>14 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={featureScale}
          exclusive
          onChange={(_, v) => v && setFeatureScale(v)}
          size="small"
        >
          <ToggleButton value="linear" sx={{ textTransform: 'none', px: 1.5 }}>Linear</ToggleButton>
          <ToggleButton value="log" sx={{ textTransform: 'none', px: 1.5 }}>Log</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loadingFeatureUsage ? (
        <Skeleton variant="rounded" height={300} sx={{ mb: 3 }} />
      ) : featureUsage?.totals.length ? (
        <>
          {/* Bar chart: page views per feature */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Page Views by Feature
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(200, featureUsage.totals.length * 36)}>
                <BarChart data={featureScale === 'log' ? featureUsage.totals.map(t => ({ ...t, _logCount: t.count + 1 })) : featureUsage.totals} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={muiTheme.palette.divider} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: chartLabelColor }}
                    scale={featureScale}
                    domain={featureScale === 'log' ? [1, 'auto'] : undefined}
                    tickFormatter={featureScale === 'log' ? (v: number) => String(Math.max(0, Math.round(v - 1))) : undefined}
                  />
                  <YAxis type="category" dataKey="page" width={80} tick={{ fontSize: 11, fill: chartLabelColor }} />
                  <Tooltip
                    formatter={featureScale === 'log' ? (v: number) => [Math.max(0, Math.round(v - 1)), 'Views'] : undefined}
                    contentStyle={{ backgroundColor: muiTheme.palette.background.paper, borderColor: muiTheme.palette.divider, color: muiTheme.palette.text.primary }} labelStyle={{ color: muiTheme.palette.text.primary }} itemStyle={{ color: muiTheme.palette.text.secondary }}
                  />
                  <Bar dataKey={featureScale === 'log' ? '_logCount' : 'count'} name="Views" radius={[0, 4, 4, 0]}>
                    {featureUsage.totals.map((entry, index) => (
                      <Cell key={index} fill={featureColorMap.get(entry.page) || COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Trend line chart for top features */}
          {featureTimeSeriesData.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Daily Trend (Top 5 Features)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={featureTimeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={muiTheme.palette.divider} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v: string) => {
                        const d = new Date(v + 'T00:00:00');
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                      tick={{ fontSize: 11, fill: chartLabelColor }}
                      interval="preserveStartEnd"
                    />
                    <YAxis allowDecimals={false} tick={{ fill: chartLabelColor }} />
                    <Tooltip
                      labelFormatter={(v: string) => {
                        const d = new Date(v + 'T00:00:00');
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      }}
                      contentStyle={{ backgroundColor: muiTheme.palette.background.paper, borderColor: muiTheme.palette.divider, color: muiTheme.palette.text.primary }} labelStyle={{ color: muiTheme.palette.text.primary }} itemStyle={{ color: muiTheme.palette.text.secondary }}
                    />
                    <Legend wrapperStyle={{ color: chartLabelColor }} />
                    {featureSeriesKeys.map((key) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={featureColorMap.get(key) || COLORS[0]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No feature usage data for this period
            </Typography>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 3 }} />

      {/* API Usage Chart */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          API Usage
        </Typography>
        <Button size="small" startIcon={<RestartAlt sx={{ fontSize: 16 }} />} onClick={resetApiSettings} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
          Reset
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink>Service</InputLabel>
          <Select value={apiProvider} label="Service" displayEmpty onChange={(e) => setApiProvider(e.target.value)}>
            <MenuItem value="">All Services</MenuItem>
            {apiUsage?.providers.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink>API Type</InputLabel>
          <Select value={apiType} label="API Type" displayEmpty onChange={(e) => setApiType(e.target.value)}>
            <MenuItem value="">All Types</MenuItem>
            {apiUsage?.apiTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel shrink>Trigger</InputLabel>
          <Select value={apiTrigger} label="Trigger" displayEmpty onChange={(e) => setApiTrigger(e.target.value)}>
            <MenuItem value="">All Triggers</MenuItem>
            {apiUsage?.triggers.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Group by</InputLabel>
          <Select value={apiGroupBy} label="Group by" onChange={(e) => setApiGroupBy(e.target.value as typeof apiGroupBy)}>
            <MenuItem value="service">Service</MenuItem>
            <MenuItem value="apiType">API Type</MenuItem>
            <MenuItem value="trigger">Trigger</MenuItem>
            <MenuItem value="statusCode">Status Code</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Range</InputLabel>
          <Select value={apiDays} label="Range" onChange={(e) => setApiDays(e.target.value as number)}>
            <MenuItem value={1}>24 hours</MenuItem>
            <MenuItem value={3}>3 days</MenuItem>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={14}>14 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Granularity</InputLabel>
          <Select value={apiGranularity} label="Granularity" onChange={(e) => setApiGranularity(e.target.value as 'daily' | 'hourly')}>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="hourly">Hourly</MenuItem>
          </Select>
        </FormControl>
        {apiGranularity === 'hourly' && (
          <ToggleButtonGroup
            value={apiTimezone}
            exclusive
            onChange={(_, v) => v && setApiTimezone(v)}
            size="small"
          >
            <ToggleButton value="local" sx={{ textTransform: 'none', px: 1.5 }}>Local</ToggleButton>
            <ToggleButton value="utc" sx={{ textTransform: 'none', px: 1.5 }}>UTC</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {loadingApiUsage ? (
            <Skeleton variant="rounded" height={300} />
          ) : apiUsage?.stats.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={apiUsage.stats}>
                <CartesianGrid strokeDasharray="3 3" stroke={muiTheme.palette.divider} />
                <XAxis dataKey="date" tickFormatter={formatXLabel} tick={{ fontSize: 11, fill: chartLabelColor }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} label={yAxisLabel} tick={{ fill: chartLabelColor }} />
                <Tooltip labelFormatter={formatXLabel} contentStyle={{ backgroundColor: muiTheme.palette.background.paper, borderColor: muiTheme.palette.divider, color: muiTheme.palette.text.primary }} labelStyle={{ color: muiTheme.palette.text.primary }} itemStyle={{ color: muiTheme.palette.text.secondary }} />
                <Legend wrapperStyle={{ color: chartLabelColor }} />
                {seriesKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                    isAnimationActive={!chartHasAnimated}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No API usage data for this period
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InsightsTab;
